/*!
 * The SigmaBot builder of the pinball table: a spec becomes a three.js group.
 *
 * Ported from sigmashake-vcs web/bots/three/build.ts (the node tree and the part rules: a loft
 * body, arms, feet, a face, a hat and an accessory).
 * Upstream project: ClaudeAnimationBase - https://github.com/JohnHeibel/ClaudeAnimationBase
 * MIT License. Copyright (c) 2026 John Heibel. The full MIT license text is in NOTICE.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */
// SPDX-License-Identifier: GPL-3.0-or-later
// Changes from upstream: the table has no parts file (GLB), no toon shader, no pose system, no
// decals, no clothes and no held items. Hats, eyes, mouths and accessories are small groups of
// primitives (face.ts, hats.ts, accessories.ts). The materials are MeshStandardMaterial, so the
// table lights apply. The bot is static: the character code moves the nodes.
// Rules for this file: no clock, no random, no DOM. The same spec and scale give the same mesh.
//
// Node tree (all names are exported in SIGMABOT_NODES):
//   SigmaBot (root, scaled)
//     SigmaBotBody        the loft solid, with a white outline hull
//     SigmaBotExtras      ears or bolts of the body
//     SigmaBotFeet        two feet
//     SigmaBotArms        two arms (SigmaBotArmL, SigmaBotArmR), each with a hand
//     SigmaBotFace        eyes and mouth
//     SigmaBotHat         the hat (none: no node)
//     SigmaBotAccessory   the accessory (none: no node)
// Body units: 1 unit is `scale` table units. Up is +y. The front is +z. The origin is the foot.

import {
  BoxGeometry,
  BufferGeometry,
  CapsuleGeometry,
  ConeGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  type Object3D,
  SphereGeometry,
} from "three";
import { outlined, put, solid } from "../common.ts";
import { mergeStatic, shareMaterials } from "../merge.ts";
import { addAccessory } from "./accessories.ts";
import { BODIES, type BodyDef } from "./bodies.ts";
import { addEyes, addMouth } from "./face.ts";
import { addHat } from "./hats.ts";
import { type Colors, colorsFor, hexOf, hsl, type Rgba } from "./kit.ts";
import { loftBody } from "./loft.ts";
import { part } from "./parts.ts";
import { normalizeSpec, type SigmaBotSpec } from "./spec.ts";

/** Names of the nodes of a SigmaBot. */
export const SIGMABOT_NODES = {
  root: "SigmaBot",
  body: "SigmaBotBody",
  extras: "SigmaBotExtras",
  feet: "SigmaBotFeet",
  arms: "SigmaBotArms",
  armL: "SigmaBotArmL",
  armR: "SigmaBotArmR",
  face: "SigmaBotFace",
  hat: "SigmaBotHat",
  accessory: "SigmaBotAccessory",
} as const;

/** Key in `Object3D.userData` that holds the `SigmaBotInfo` of a SigmaBot root. */
export const SIGMABOT_KEY = "sigmabot";

/** What a caller may need to place a prop on a SigmaBot. All sizes are body units (not scaled). */
export interface SigmaBotInfo {
  readonly spec: SigmaBotSpec;
  /** Height of the body top. */
  readonly bodyHeight: number;
  /** Half width of the widest part of the body. */
  readonly halfWidth: number;
}

/** Read the info of a SigmaBot root. Null when the object is not a SigmaBot. */
export function sigmaBotInfo(obj: Object3D): SigmaBotInfo | null {
  const value: unknown = obj.userData[SIGMABOT_KEY];
  return value !== undefined && value !== null && typeof value === "object"
    ? (value as SigmaBotInfo)
    : null;
}

const PINK_IN: Rgba = hsl(345, 80, 80);

/** The loft solid of the body as a three.js geometry. */
function bodyGeometry(body: string): BufferGeometry {
  const data = loftBody(body);
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(data.positions, 3));
  g.setAttribute("normal", new Float32BufferAttribute(data.normals, 3));
  g.setIndex(data.indices);
  g.computeBoundingBox();
  return g;
}

/** Ears of a bunny or a cat, or the bolts of a robot. */
function addExtras(parent: Group, b: BodyDef, c: Colors): void {
  const g = new Group();
  g.name = SIGMABOT_NODES.extras;
  for (const s of [-1, 1]) {
    if (b.extra === "bunnyEars") {
      part(g, new SphereGeometry(1, 8, 6), c.main, s * 13, 88, 0, {
        sx: 8,
        sy: 22,
        sz: 5,
        rz: -s * 0.12,
      });
      part(g, new SphereGeometry(1, 8, 6), PINK_IN, s * 13, 87, 2.5, {
        sx: 4,
        sy: 15,
        sz: 2.5,
        rz: -s * 0.12,
      });
    } else if (b.extra === "catEars") {
      part(g, new ConeGeometry(9, 26, 4), c.main, s * 27, 64, 0, { rz: -s * 0.35, sz: 0.5 });
      part(g, new ConeGeometry(5, 17, 4), PINK_IN, s * 26, 62, 2.4, { rz: -s * 0.35, sz: 0.4 });
    } else if (b.extra === "robotBolts") {
      part(g, new BoxGeometry(10, 18, 10), c.accentDark, s * 30, 37, 0);
    }
  }
  if (b.extra) parent.add(g);
}

/** Two arms that hang from the shoulders, a little out from the body, each with a hand. */
function addArms(parent: Group, b: BodyDef, c: Colors): void {
  const arms = new Group();
  arms.name = SIGMABOT_NODES.arms;
  const radius = b.armLen < 12 ? 2.6 : 3.2;
  for (const s of [-1, 1] as const) {
    const arm = new Group();
    arm.name = s < 0 ? SIGMABOT_NODES.armL : SIGMABOT_NODES.armR;
    arm.position.set(s * b.armDx, -b.shoulderY, 0);
    arm.rotation.z = s * 0.3;
    arm.add(
      put(
        new CapsuleGeometry(radius, Math.max(0.1, b.armLen - 2 * radius), 2, 8),
        solid(hexOf(c.shade)),
        0,
        -b.armLen / 2,
        0,
      ),
    );
    part(arm, new SphereGeometry(radius + 1.2, 8, 6), c.light, 0, -b.armLen - 1.2, 0);
    arms.add(arm);
  }
  parent.add(arms);
}

/** Two flat feet on the ground. */
function addFeet(parent: Group, b: BodyDef, c: Colors): void {
  const feet = new Group();
  feet.name = SIGMABOT_NODES.feet;
  for (const s of [-1, 1]) {
    part(feet, new SphereGeometry(5.5, 8, 6), c.shade, s * b.footDx, 3, 2, { sy: 0.6, sz: 1.3 });
  }
  parent.add(feet);
}

/** Nodes whose parts are static, so each material of a node becomes one mesh. */
const STATIC_NODES: readonly string[] = [
  SIGMABOT_NODES.extras,
  SIGMABOT_NODES.feet,
  SIGMABOT_NODES.face,
  SIGMABOT_NODES.hat,
  SIGMABOT_NODES.accessory,
];

/**
 * Cut the draw cost of a built bot: meshes of one colour share a material, and the static parts
 * of a node that share a material become one mesh. The arms stay apart, because code turns them.
 */
function slimBot(root: Group): void {
  shareMaterials(root);
  for (const name of STATIC_NODES) {
    const node = root.getObjectByName(name);
    if (node) mergeStatic(node, name);
  }
}

/**
 * Build one SigmaBot from a spec. `scale` is the size of one body unit in table units (a blob is
 * about 60 body units tall). The group origin is the foot anchor (bottom center). The bot looks
 * toward +z. The result is the same for the same spec and scale.
 *
 * An unknown name in the spec falls back to the first name of its list. A scale that is not a
 * positive number gives 1.
 */
export function buildSigmaBot(spec: SigmaBotSpec, scale = 1): Group {
  const s = normalizeSpec(spec);
  const b = BODIES[s.body];
  const c = colorsFor(s.palette);
  const root = new Group();
  root.name = SIGMABOT_NODES.root;
  root.scale.setScalar(Number.isFinite(scale) && scale > 0 ? scale : 1);

  const body = outlined(new Mesh(bodyGeometry(s.body), solid(hexOf(c.main))));
  body.name = SIGMABOT_NODES.body;
  root.add(body);
  addExtras(root, b, c);
  addFeet(root, b, c);
  addArms(root, b, c);

  const face = new Group();
  face.name = SIGMABOT_NODES.face;
  addEyes(face, s.body, b, s.eyes);
  addMouth(face, s.body, b, s.mouth);
  root.add(face);

  addHat(root, s.body, b, c, s.hat);
  addAccessory(root, s.body, b, c, s.accessory);
  slimBot(root);

  root.userData[SIGMABOT_KEY] = {
    spec: s,
    bodyHeight: -b.topY,
    halfWidth: b.rx,
  } satisfies SigmaBotInfo;
  return root;
}
